import {
  PrintingLabelType,
  PrintingPermissions,
  transformPrintingPermissions,
} from '../../Api/asn';
import { User } from '../../Api/vendor';

function getTransformedPermissions(user: User): PrintingPermissions {
  return transformPrintingPermissions(user?.vendor?.printing_permissions);
}

export function isLabelApproved(
  permissions: PrintingPermissions | undefined,
  labelType: PrintingLabelType,
): boolean {
  if (!permissions) return false;
  return permissions[labelType]?.status === 'approved';
}

export function isUnitLabelApproved(user: User): boolean {
  return isLabelApproved(getTransformedPermissions(user), 'unit_label');
}

export function isHangtagApproved(user: User): boolean {
  return isLabelApproved(getTransformedPermissions(user), 'hangtag');
}

export function isCartonIcclApproved(user: User): boolean {
  return isLabelApproved(getTransformedPermissions(user), 'carton_iccl');
}

export function isCartonAsnApproved(user: User): boolean {
  return isLabelApproved(getTransformedPermissions(user), 'carton_asn');
}

export function isCartonApproved(user: User): boolean {
  return isCartonIcclApproved(user) || isCartonAsnApproved(user);
}

export function hasAnyPrintingApproved(user: User): boolean {
  return (
    isUnitLabelApproved(user) ||
    isHangtagApproved(user) ||
    isCartonApproved(user)
  );
}

export function isPrintingApproved(
  user: User,
  currentUser?: User | null,
): boolean {
  const hasInternalPrintPermission = Boolean(
    currentUser?.feature_permissions?.internal_print,
  );

  const hasOnboardingApproval = Boolean(
    user?.vendor?.printing_onboarding?.approved_at &&
      user?.feature_permissions?.allow_printing,
  );

  const hasGranularApproval =
    hasAnyPrintingApproved(user) && user?.feature_permissions?.allow_printing;

  return Boolean(
    hasInternalPrintPermission || hasOnboardingApproval || hasGranularApproval,
  );
}

export function hasLegacyPrintingApproval(user: User): boolean {
  return Boolean(user?.vendor?.printing_onboarding?.approved_at);
}

export function canPrintLabelType(
  user: User,
  labelType: PrintingLabelType,
  currentUser?: User | null,
): boolean {
  const hasInternalPrintPermission = Boolean(
    currentUser?.feature_permissions?.internal_print,
  );

  if (hasInternalPrintPermission) {
    return true;
  }

  if (!user?.feature_permissions?.allow_printing) {
    return false;
  }

  if (hasLegacyPrintingApproval(user)) {
    return true;
  }

  return isLabelApproved(getTransformedPermissions(user), labelType);
}

export function canPrintUnitLabel(
  user: User,
  currentUser?: User | null,
): boolean {
  return canPrintLabelType(user, 'unit_label', currentUser);
}

export function canPrintHangtag(
  user: User,
  currentUser?: User | null,
): boolean {
  return canPrintLabelType(user, 'hangtag', currentUser);
}

export function canPrintCartonIccl(
  user: User,
  currentUser?: User | null,
): boolean {
  return canPrintLabelType(user, 'carton_iccl', currentUser);
}

export function canPrintCartonAsn(
  user: User,
  currentUser?: User | null,
): boolean {
  return canPrintLabelType(user, 'carton_asn', currentUser);
}

export function canPrintCarton(user: User, currentUser?: User | null): boolean {
  return (
    canPrintCartonIccl(user, currentUser) ||
    canPrintCartonAsn(user, currentUser)
  );
}
